import React from "react";
import { jsPDF } from "jspdf";
import notoSansJP from "../fonts/NotoSansJP-Regular.ttf";
import { getLabels, onSubmitDB } from "../logic/ServerCalls";
import "./css/pdf.css";
import FormRes from "./FormRes/FormRes.js";
import Header from "./Header.js";
const ReactDomServer = require("react-dom/server");

// jsPDF's built-in fonts can't display Japanese text, so JP orders use this font instead.
const jpFontFaces = [
  {
    family: "Noto Sans JP",
    src: [{ url: notoSansJP, format: "truetype" }],
  },
];

/**
 *
 * @param {Array} otherFiles - Other files uploaded by seller that they want hcs to have
 * @param {Object} info - info about company, country and form-type
 * @param {Object} props - the props from formRes
 * @param {Boolean} ownCopy - If we should create a copy of the order for the seller to download
 * @param {Boolean} quote - If we should create a quoute that the seller can download
 * @param {Boolean} order - If we should send a order to hcs
 *
 * Creates two diffrent pdf's, two order and one quote pdf.
 */

export async function sendOrder(data, ownCopy, serverResponce) {
  try {
    let labels = await getLabels(data.params.lang.toLowerCase());

    let orderPDF = new jsPDF("p", "px", "a4");
    const contractNumber = data.formInformation.contractNumber;
    const lang = data.params.lang.toLowerCase();
    const isNordicLeaseplus =
      data.params.formType === "leaseplus" &&
      (lang === "se" || lang === "no" || lang === "dk");

    // SE/NO/DK leaseplus cases aren't created in DP yet, so no contract number
    const contractNumberSuffix =
      contractNumber && !isNordicLeaseplus ? `_${contractNumber}` : "";

    const filename = `${data.params.formType}_order${contractNumberSuffix}.pdf`;
    const creditFileName = `credit${contractNumberSuffix}.pdf`;

    const isJapanese = lang === "jp";
    const formRes = ReactDomServer.renderToString(
      <div className={isJapanese ? "pdf-jp-font" : undefined}>
        <Header
          imageSrc={data.headerImgSrc}
          params={data.params}
          imageWidth={"100px"}
          headerType={"header-order"}
        />
        <FormRes
          formInformation={data.formInformation}
          productsValues={data.productsValues}
          customerInformation={data.customerInformation}
          labels={labels}
          accessories={data.accessories}
          params={data.params}
          totalResidue={data.totalResidue}
          totalPrice={data.totalPrice}
          extraFiles={data.extraFiles}
          pdf={true}
          products={data.products}
          productAccessories={data.productAccessories}
          variables={data.variables}
          newDealerInformation={data.newDealerInformation}
        />
      </div>,
    );
    return new Promise((resolve, reject) => {
      orderPDF.html(formRes, {
        fontFaces: isJapanese ? jpFontFaces : undefined,
        callback: async function (pdf) {
          try {
            if (data.contractFile.file === null) {
              data.contractFile.file = new Blob([], {
                type: "application/pdf",
              });
            }
            if (data.creditFile?.file === null) {
              data.creditFile.file = new Blob([], { type: "application/pdf" });
            }

            let filenames;
            let mandatoryFiles;
            const lang = data.params.lang.toLowerCase();
            const formType = data.params.formType.toLowerCase();

            if (lang === "fr") {
              filenames = [
                filename,
                data.transferAgreementFile?.fileName,
                data.factureFictive?.fileName,
                data.contractFile.fileName,
                /* data.creditFile.fileName,   Exclude credit file for FR orders */
              ];
              mandatoryFiles = [
                pdf.output("blob"),
                data.transferAgreementFile?.file,
                data.factureFictive?.file,
                data.contractFile.file,
                /* data.creditFile.file,       Exclude credit file for FR orders  */
              ];
            } else if (lang === "de") {
              filenames = [
                filename,
                data.transferAgreementFile?.fileName,
                data.factureFictive?.fileName,
                data.contractFile.fileName,
                creditFileName,
              ];
              mandatoryFiles = [
                pdf.output("blob"),
                data.transferAgreementFile?.file,
                data.factureFictive?.file,
                data.contractFile.file,
                data.creditFile.file,
              ];
            } else if (
              (lang === "uk" ||
                lang === "se" ||
                lang === "no" ||
                lang === "dk" ||
                lang === "jp") &&
              formType === "upcare"
            ) {
              filenames = [
                filename,
                data.factureFictive?.fileName,
                data.hireAgreementFile?.fileName,
              ];
              mandatoryFiles = [
                pdf.output("blob"),
                data.factureFictive?.file,
                data.hireAgreementFile?.file,
              ];
            } else if (lang === "uk") {
              filenames = [
                filename,
                data.transferAgreementFile?.fileName,
                data.factureFictive?.fileName,
                data.contractFile?.fileName,
                data.preContractFile?.fileName,
              ];
              mandatoryFiles = [
                pdf.output("blob"),
                data.transferAgreementFile?.file,
                data.factureFictive?.file,
                data.contractFile?.file,
                data.preContractFile?.file,
              ];
            } else {
              filenames = [
                filename,
                data.contractFile.fileName,
                creditFileName,
              ];
              mandatoryFiles = [
                pdf.output("blob"),
                data.contractFile.file,
                data.creditFile.file,
              ];
            }

            const filesToSend = mandatoryFiles.concat(
              data.extraFiles,
              data.payslipFiles || [],
            );
            for (const file of data.extraFiles) {
              const name = file.fileName || file.name;
              if (name) filenames.push(name);
            }
            for (const file of data.payslipFiles || []) {
              const name = file.fileName || file.name;
              if (name) filenames.push(name);
            }

            const files = await convertFileToBase64(filesToSend, filenames);
            let res = await onSubmitDB(files, data.params, data);

            if (res.status === 200 && ownCopy) {
              pdf.save(filename);
            }

            await serverResponce(res);
            resolve(res);
          } catch (err) {
            console.error("sendOrder callback error:", err);
            reject(err);
          }
        },
        x: 0,
        y: 0,
      });
    });
  } catch (err) {
    console.error("sendOrder outer error:", err);
    throw err;
  }
}
export async function generateOrderPDF(data) {
  let labels = await getLabels(data.params.lang.toLowerCase());

  let orderPDF = new jsPDF("p", "px", "a4");
  const isJapanese = data.params.lang.toLowerCase() === "jp";

  const formRes = ReactDomServer.renderToString(
    <div className={isJapanese ? "pdf-jp-font" : undefined}>
      <Header
        imageSrc={data.headerImgSrc}
        params={data.params}
        imageWidth={"100px"}
        headerType={"header-order"}
      />
      <FormRes
        formInformation={data.formInformation}
        productsValues={data.productsValues}
        customerInformation={data.customerInformation}
        labels={labels}
        accessories={data.accessories}
        params={data.params}
        totalResidue={data.totalResidue}
        totalPrice={data.totalPrice}
        extraFiles={data.extraFiles}
        pdf={true}
        products={data.products}
        productAccessories={data.productAccessories}
        variables={data.variables}
        newDealerInformation={data.newDealerInformation}
      />
    </div>,
  );

  return new Promise((resolve) => {
    orderPDF.html(formRes, {
      fontFaces: isJapanese ? jpFontFaces : undefined,
      callback: async function (pdf) {
        const orderPdfBlob = pdf.output("blob");
        resolve(orderPdfBlob);
      },
      x: 0,
      y: 0,
    });
  });
}

const convertFileToBase64 = async (files, filenames) => {
  const base64Strings = await Promise.all(
    files.map((file, index) => {
      return new Promise((resolve, reject) => {
        if (!file) return resolve(null);
        let finalFileName = file.name;
        const reader = new FileReader();

        reader.onloadend = () => {
          try {
            const base64String = btoa(
              new Uint8Array(reader.result).reduce(
                (data, byte) => data + String.fromCharCode(byte),
                "",
              ),
            );

            if (!finalFileName) {
              finalFileName = filenames[index];
            }

            if (!finalFileName) {
              return resolve(null);
            }

            resolve({
              data: base64String,
              type: file.type,
              fileName: finalFileName,
            });
          } catch (err) {
            reject(
              new Error(
                `Error converting file ${finalFileName}: ${err.message}`,
              ),
            );
          }
        };

        reader.onerror = () => {
          reject(
            new Error(
              `FileReader error reading file ${
                finalFileName || filenames[index] || "unknown"
              }`,
            ),
          );
        };

        reader.readAsArrayBuffer(file);
      });
    }),
  );

  return base64Strings.filter((result) => result !== null);
};
